import { motion } from "framer-motion";
import { Wrench, Engine, GasPump, Gauge, CarSimple, Gear } from "@phosphor-icons/react";

const SERVICES = [
  {
    icon: Engine,
    title: "Diagnostics & \"No-Start\"",
    description: "Expert troubleshooting for frozen vehicles, dead batteries, and failed starters—often isolating the issue in under 30 minutes.",
  },
  {
    icon: Wrench,
    title: "Suspension & Brakes",
    description: "Full-service replacement of struts, brakes, and rotors, performed even in tight residential garage settings.",
  },
  {
    icon: Gear,
    title: "Advanced Part Procurement",
    description: "We handle the logistics of getting parts (like AutoZone battery swaps) so you don't have to leave your home.",
  },
  {
    icon: CarSimple,
    title: "In-Shop Maintenance",
    description: "Dedicated facility in the Hillsboro area for more intensive mechanical work that can't be done on-site.",
  },
  {
    icon: GasPump,
    title: "Routine Maintenance",
    description: "Professional oil changes, fluid flushes, and scheduled maintenance performed at your convenience.",
  },
  {
    icon: Gauge,
    title: "Rescue Services",
    description: "Specialized in saving \"DIY\" projects gone wrong. We'll pick up where you left off and get it done right.",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1 },
  }),
};

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="uppercase text-xs tracking-[0.2em] text-muted-foreground font-medium mb-4"
        >
          Our Services
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-none mb-6 max-w-xl"
        >
          Everything your car needs, <span className="text-primary">nothing it doesn't</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-muted-foreground leading-relaxed mb-16 max-w-lg text-sm md:text-base"
        >
          From routine maintenance to complex engine work — we handle standard and high-end vehicles with the same care and transparency.
        </motion.p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((service, i) => (
            <motion.div
              key={service.title}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariants}
              className="bg-card border border-stone-200 rounded-xl p-8 group hover:border-primary/30 transition-colors duration-300"
              data-testid={`service-card-${i}`}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors duration-300">
                <service.icon weight="duotone" className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-lg mb-2 tracking-tight">{service.title}</h3>
              <p className="text-muted-foreground leading-relaxed text-sm">{service.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center text-muted-foreground text-sm mt-10"
        >
          Need a master-level rescue? Call Gerron at{" "}
          <a href="tel:+15033805413" className="text-primary font-medium hover:underline" data-testid="services-phone-link">
            (503) 380-5413
          </a>{" "}
          — professional execution at your location.
        </motion.p>
      </div>
    </section>
  );
}
